import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import indexPriority from 'theme/z-index';
import { useShowSearchOverlay } from 'context/general/ShowSearchOverlay';
import { useEventDetailModal } from 'context/events/EventDetailModal';
import paths from 'routes/paths';
import { useEventsList } from 'context/events/EventsList';
import { useEstablishmentsList } from 'context/establishments/EstablishmentsList';
import { useEventFilter } from 'context/events/EventFilter';
import { useEstablishmentFilter } from 'context/establishments/EstablishmentFilter';
import eventsApi from 'api/requests/events';
import establishmentsApi from 'api/requests/establishments';
import tagsApi from 'api/requests/tags';
import { useSearchSession } from 'helpers/searchLog';
import SearchOverlayResults from './SearchOverlayResults';

export default function SearchOverlay({ onRegisterSearch }) {
    const { showSearchOverlay, setShowSearchOverlay } = useShowSearchOverlay();
    const { setEventDetailModal } = useEventDetailModal();
    const location = useLocation();
    const navigate = useNavigate();
    const isEstablishment = location.pathname.includes('/estabelecimentos');

    const { eventsList } = useEventsList();
    const { establishmentsList } = useEstablishmentsList();
    const { eventFilter, setEventFilter } = useEventFilter();
    const { establishmentFilter, setEstablishmentFilter } = useEstablishmentFilter();

    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [activeTag, setActiveTag] = useState(null);
    const [tags, setTags] = useState(null);
    const searchSession = useSearchSession();

    const featuredItems = isEstablishment
        ? [
            ...(establishmentsList?.data || []),
            ...(establishmentsList?.withMultiples?.flatMap(g => g.multiples || []) || [])
          ].slice(0, 8)
        : [
            ...(eventsList?.data || []),
            ...(eventsList?.withMultiples?.flatMap(g => g.multiples || []) || [])
          ].slice(0, 8);

    // Register search handler so HomeHeader can trigger it from its input
    useEffect(() => {
        onRegisterSearch?.((term) => {
            setSearchTerm(term);
            if (term) fetchResults(term, activeTag);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTag]);

    // Fetch tags when overlay opens
    useEffect(() => {
        if (showSearchOverlay && tags === null) fetchTags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSearchOverlay]);

    // Escape key closes the overlay
    useEffect(() => {
        if (!showSearchOverlay) return;
        function onKeyDown(e) {
            if (e.key === 'Escape') close();
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [showSearchOverlay]);

    async function fetchTags() {
        try {
            if (isEstablishment) {
                const { data } = await establishmentsApi.getTags();
                setTags(data?.data || []);
            } else {
                const { data } = await tagsApi.get(1);
                setTags(data || []);
            }
        } catch (e) {
            setTags([]);
        }
    }

    async function fetchResults(term, tag) {
        setIsSearching(true);
        setHasSearched(true);
        try {
            // `search_session`: a API registra a busca (termo, filtros, quantos achou).
            const params = { limit: 8, page: 1, search_session: searchSession.sessionFor() };
            if (term) params.term = term;

            let res;
            if (isEstablishment) {
                const filterTags = establishmentFilter?.tags?.length ? establishmentFilter.tags.join(',') : null;
                const mergedTags = [tag, filterTags].filter(Boolean).join(',');
                if (mergedTags) params.tags = mergedTags;
                if (establishmentFilter?.features?.length) params.features = establishmentFilter.features.join(',');
                if (establishmentFilter?.dates) params.filter_date = establishmentFilter.dates;
                const { data } = await establishmentsApi.filter(params);
                res = data;
            } else {
                const filterCats = eventFilter?.categories?.length ? eventFilter.categories.join(',') : null;
                const mergedTags = [tag, filterCats].filter(Boolean).join(',');
                if (mergedTags) params.tags = mergedTags;
                if (eventFilter?.dates) params.filter_date = eventFilter.dates;
                if (eventFilter?.classification_id) params.classification_id = eventFilter.classification_id;
                if (eventFilter?.distance_range) params.distance_range = eventFilter.distance_range;
                const { data } = await eventsApi.filter(params);
                res = data;
            }
            setResults(res?.data || []);
        } catch (e) {
            setResults([]);
        } finally {
            setIsSearching(false);
        }
    }

    // Re-run active search when filters change while overlay is open
    useEffect(() => {
        if (!showSearchOverlay || !hasSearched) return;
        fetchResults(searchTerm, activeTag);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventFilter, establishmentFilter]);

    function handleTagClick(tagName) {
        const newTag = activeTag === tagName ? null : tagName;
        setActiveTag(newTag);
        if (newTag) {
            fetchResults(searchTerm.trim(), newTag);
        } else if (searchTerm.trim()) {
            fetchResults(searchTerm.trim(), null);
        } else {
            setHasSearched(false);
            setResults([]);
            searchSession.reset();
        }
    }

    function handleVerTodos() {
        const params = new URLSearchParams();
        if (searchTerm.trim()) params.set('search', searchTerm.trim());
        if (activeTag) params.set('tags', activeTag);
        const path = isEstablishment
            ? `${paths.establishments.search}?${params.toString()}`
            : `${paths.events.search}?${params.toString()}`;
        navigate(path);
        close();
    }

    function handleCardClick(item, position) {
        // Só o que veio de uma busca conta; os destaques, antes de buscar, não.
        if (hasSearched) {
            searchSession.logClick(isEstablishment ? 'establishment' : 'event', item, position);
        }

        if (isEstablishment) {
            navigate(`/estabelecimento/${item.slug}/detalhes`);
            close();
        } else {
            close();
            setEventDetailModal(item);
        }
    }

    function close() {
        setShowSearchOverlay(false);
        setInputValue('');
        setSearchTerm('');
        setResults([]);
        setHasSearched(false);
        setActiveTag(null);
        setEventFilter(null);
        setEstablishmentFilter(null);
        searchSession.reset();
    }

    return (
        <>
            <Backdrop $open={showSearchOverlay} onClick={close} />
            <Panel $open={showSearchOverlay}>
                <MobileSearchBar>
                    <Search size={16} color={colors.night} />
                    <MobileInput
                        type="text"
                        placeholder={isEstablishment ? 'Buscar estabelecimentos...' : 'Buscar eventos...'}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        onKeyUp={e => {
                            if (e.key === 'Enter' && inputValue.trim()) {
                                setSearchTerm(inputValue.trim());
                                fetchResults(inputValue.trim(), activeTag);
                            }
                        }}
                        autoFocus={false}
                    />
                    {inputValue && (
                        <ClearInput onClick={() => { setInputValue(''); setSearchTerm(''); setResults([]); setHasSearched(false); searchSession.reset(); }}>
                            <X size={14} color={colors.night} />
                        </ClearInput>
                    )}
                </MobileSearchBar>
                <SearchOverlayResults
                    featuredItems={featuredItems}
                    results={results}
                    hasSearched={hasSearched}
                    isSearching={isSearching}
                    tags={tags}
                    activeTag={activeTag}
                    searchTerm={searchTerm}
                    isEstablishment={isEstablishment}
                    onTagClick={handleTagClick}
                    onCardClick={handleCardClick}
                    onVerTodos={handleVerTodos}
                    onClose={close}
                />
            </Panel>
        </>
    );
}

const Backdrop = styled.div`
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: block;
        position: fixed;
        top: 80px;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.45);
        z-index: ${indexPriority.searchOverlayBackdrop};
        opacity: ${({ $open }) => $open ? 1 : 0};
        pointer-events: ${({ $open }) => $open ? 'auto' : 'none'};
        transition: opacity 0.2s ease;
    }
`;

const Panel = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${colors.neutralBackground};
    z-index: ${indexPriority.searchOverlay};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transform: ${({ $open }) => $open ? 'translateY(0)' : 'translateY(-110%)'};
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: ${({ $open }) => $open ? 'auto' : 'none'};

    @media screen and (min-width: ${breakpoints.lg}) {
        top: 80px;
        bottom: auto;
        overflow: visible;
        border-radius: 0 0 16px 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    }
`;

const MobileSearchBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: ${colors.neutralDark};
    border-radius: 50px;
    margin: 16px 16px 0;
    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileInput = styled.input`
    flex: 1;
    border: none;
    background: transparent;
    font-size: 15px;
    color: ${colors.nightDark};
    outline: none;
    &::placeholder { color: ${colors.nightLight}; }
`;

const ClearInput = styled.button`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
`;
